/**
 * Aynı ekrandaki diğer kişiler — grid araç çubuğunun sağında avatar yığını.
 * Düzenleme yapan kişinin halkası turuncu ve köşesinde kalem işareti var.
 */
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { Avatar } from '@/shared/ui/Avatar';
import { Tooltip } from '@/shared/ui/Tooltip';
import { cn } from '@/shared/lib/cn';
import { minutesAgo, useUserPhoto, type PresencePerson } from '../model/presenceHooks';

const MAX_SHOWN = 4;

function PersonAvatar({ person, className }: { person: PresencePerson; className?: string }) {
  const photo = useUserPhoto(person.userId);
  return (
    // Düzenleyen kişi öne alınır: kalem işareti yanındaki avatarın altında kalmasın.
    <span className={cn('relative inline-flex', person.editing && 'z-10', className)}>
      <Avatar
        name={person.userName}
        imageBase64={photo}
        className={cn('ring-bg-elevated h-6 w-6 text-[10px] ring-2', person.editing && 'ring-amber-500')}
      />
      {person.editing ? (
        <span className="absolute -right-0.5 -bottom-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-amber-500 text-white">
          <Pencil className="h-2 w-2" />
        </span>
      ) : null}
    </span>
  );
}

export function PresenceAvatars({ people }: { people: PresencePerson[] }) {
  const { t } = useTranslation();
  if (!people.length) return null;
  const shown = people.slice(0, MAX_SHOWN);
  const rest = people.length - shown.length;
  const line = (p: PresencePerson) => {
    if (!p.editing) return t('presence.viewing', { name: p.userName });
    const min = minutesAgo(p.editingSince);
    return min > 0
      ? t('presence.editingFor', { name: p.userName, count: min })
      : t('presence.editing', { name: p.userName });
  };
  return (
    <Tooltip
      content={
        <div className="flex flex-col gap-0.5 text-xs">
          <span className="font-medium">{t('presence.title', { count: people.length })}</span>
          {people.map((p) => (
            <span key={p.userId}>{line(p)}</span>
          ))}
        </div>
      }
    >
      <span
        data-presence-avatars
        className="mr-1 flex shrink-0 items-center -space-x-1.5"
        aria-label={t('presence.title', { count: people.length })}
      >
        {shown.map((p) => (
          <PersonAvatar key={p.userId} person={p} />
        ))}
        {rest > 0 ? (
          <span className="bg-bg-muted text-fg-muted ring-bg-elevated relative inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold ring-2">
            +{rest}
          </span>
        ) : null}
      </span>
    </Tooltip>
  );
}
